import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import GroupsIcon from '@mui/icons-material/Groups';
import UploadIcon from '@mui/icons-material/CloudUpload';
import CampaignIcon from '@mui/icons-material/Campaign';
import NotificationAdminService from '../services/notificationAdminService';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
const FCY_FILTERS = [
  { value: 'ANY', label: 'Any (no FCY filter)' },
  { value: 'ONLY', label: 'Only first-contract-year users' },
  { value: 'EXCLUDE', label: 'Exclude first-contract-year users' },
];

const PIN_LEVELS = [
  { rank: 0,  label: 'Unknown' },
  { rank: 1,  label: '3%' },
  { rank: 2,  label: '6%' },
  { rank: 3,  label: '9%' },
  { rank: 4,  label: '12%' },
  { rank: 5,  label: '15%' },
  { rank: 6,  label: '18%' },
  { rank: 7,  label: '21%' },
  { rank: 8,  label: '23%' },
  { rank: 9,  label: '25%' },
  { rank: 10, label: 'Silver Sponsor' },
  { rank: 11, label: 'Silver' },
  { rank: 12, label: 'Gold Sponsor' },
  { rank: 13, label: 'Gold' },
  { rank: 14, label: 'Platinum' },
  { rank: 15, label: 'Ruby' },
  { rank: 16, label: 'Sapphire' },
  { rank: 17, label: 'Emerald' },
  { rank: 18, label: 'Diamond' },
  { rank: 19, label: 'Crown Ambassador' },
];

const pinLevelByRank = Object.fromEntries(PIN_LEVELS.map((p) => [p.rank, p]));

const emptyForm = {
  id: null,
  name: '',
  titleEn: '',
  titleEs: '',
  bodyEn: '',
  bodyEs: '',
  imageUrl: '',
  actionUrl: '',
  category: 'SYSTEM',
  priority: 'MEDIUM',
  targetTeamNames: [],   // array of strings
  fcyFilter: 'ANY',
  pinRankMin: '',        // '' = unset; otherwise numeric rank
  pinRankMax: '',
  targetIboNumbers: '',  // comma-separated string in UI
};

const campaignToForm = (c) => ({
  id: c.id ?? null,
  name: c.name ?? '',
  titleEn: c.titleEn ?? '',
  titleEs: c.titleEs ?? '',
  bodyEn: c.bodyEn ?? '',
  bodyEs: c.bodyEs ?? '',
  imageUrl: c.imageUrl ?? '',
  actionUrl: c.actionUrl ?? '',
  category: c.category ?? 'SYSTEM',
  priority: c.priority ?? 'MEDIUM',
  targetTeamNames: c.targetTeamNames ?? [],
  fcyFilter: c.fcyFilter ?? 'ANY',
  pinRankMin: c.pinRankMin != null ? c.pinRankMin : '',
  pinRankMax: c.pinRankMax != null ? c.pinRankMax : '',
  targetIboNumbers: (c.targetIboNumbers ?? []).join(', '),
});

const toList = (csv) =>
  (csv || '').split(',').map((s) => s.trim()).filter(Boolean);

const statusColor = (status) => {
  switch (status) {
    case 'SENT':    return 'success';
    case 'SENDING': return 'info';
    case 'FAILED':  return 'error';
    default:        return 'default';
  }
};

/* ─── Section header ──────────────────────────────────────────────────── */
const SectionLabel = ({ children }) => (
  <Typography
    variant="overline"
    sx={{ display: 'block', color: 'text.secondary', letterSpacing: 1, mb: 1.5, mt: 0.5 }}
  >
    {children}
  </Typography>
);

/* ═══════════════════════════════════════════════════════════════════════ */
const MassCommunicationPage = () => {
  const [campaigns,     setCampaigns]     = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [dialogOpen,    setDialogOpen]    = useState(false);
  const [form,          setForm]          = useState(emptyForm);
  const [saving,        setSaving]        = useState(false);
  const [uploading,     setUploading]     = useState(false);
  const [previewCount,  setPreviewCount]  = useState(null);
  const [previewing,    setPreviewing]    = useState(false);
  const [snackbar,      setSnackbar]      = useState({ open: false, message: '', severity: 'success' });

  // Teams autocomplete
  const [allTeams,      setAllTeams]      = useState([]);   // full team objects from API
  const [teamsLoading,  setTeamsLoading]  = useState(false);

  // Campaign-name search / prefill
  const [nameInput,       setNameInput]       = useState('');
  const [nameOptions,     setNameOptions]     = useState([]);  // {label, campaign}
  const nameDebounceRef = useRef(null);

  const notify = (message, severity = 'success') =>
    setSnackbar({ open: true, message, severity });

  /* ── data loaders ──────────────────────────────────────────────────── */
  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const res = await NotificationAdminService.listCampaigns(0, 100);
      setCampaigns(res?.data?.content || []);
    } catch (e) {
      notify(e?.message || 'Failed to load campaigns', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const res = await NotificationAdminService.listTeams();
      // API returns { data: [...] } or array directly — handle both
      const raw = res?.data ?? res ?? [];
      setAllTeams(Array.isArray(raw) ? raw : []);
    } catch {
      setAllTeams([]);
    } finally {
      setTeamsLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); loadTeams(); }, [loadCampaigns, loadTeams]);

  /* ── dialog helpers ────────────────────────────────────────────────── */
  const openCreate = () => {
    setForm(emptyForm);
    setNameInput('');
    setPreviewCount(null);
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    const f = campaignToForm(c);
    setForm(f);
    setNameInput(f.name);
    setPreviewCount(null);
    setDialogOpen(true);
  };

  const handleField = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  /* ── campaign-name autocomplete ────────────────────────────────────── */
  const handleNameInputChange = (_e, value, reason) => {
    setNameInput(value);
    setForm((f) => ({ ...f, name: value }));

    if (reason === 'reset') return;  // option was selected — handled below
    if (nameDebounceRef.current) clearTimeout(nameDebounceRef.current);
    nameDebounceRef.current = setTimeout(() => {
      if (!value.trim()) { setNameOptions([]); return; }
      const q = value.toLowerCase();
      const matches = campaigns
        .filter((c) => c.name?.toLowerCase().includes(q))
        .map((c) => ({ label: c.name, campaign: c }));
      setNameOptions(matches);
    }, 200);
  };

  const handleNameSelect = (_e, option) => {
    if (!option || typeof option === 'string') return;
    const f = campaignToForm(option.campaign);
    setForm({ ...f, id: null });   // clear id → creates a new campaign (prefilled copy)
    setNameInput(option.label);
    setPreviewCount(null);
  };

  /* ── payload ───────────────────────────────────────────────────────── */
  const buildPayload = () => ({
    name:             form.name,
    titleEn:          form.titleEn,
    titleEs:          form.titleEs,
    bodyEn:           form.bodyEn,
    bodyEs:           form.bodyEs,
    imageUrl:         form.imageUrl  || null,
    actionUrl:        form.actionUrl || null,
    category:         form.category,
    priority:         form.priority,
    targetTeamNames:  form.targetTeamNames,
    fcyFilter:        form.fcyFilter,
    pinRankMin:       form.pinRankMin === '' ? null : Number(form.pinRankMin),
    pinRankMax:       form.pinRankMax === '' ? null : Number(form.pinRankMax),
    targetIboNumbers: toList(form.targetIboNumbers),
  });

  /* ── image upload ──────────────────────────────────────────────────── */
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await NotificationAdminService.uploadImage(file);
      if (url) { setForm((f) => ({ ...f, imageUrl: url })); notify('Image uploaded'); }
    } catch (err) {
      notify(err?.message || 'Image upload failed', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  /* ── preview ───────────────────────────────────────────────────────── */
  const handlePreview = async () => {
    setPreviewing(true);
    try {
      const res = await NotificationAdminService.previewRecipientCount(buildPayload());
      setPreviewCount(res?.data?.recipientCount ?? 0);
    } catch (e) {
      notify(e?.message || 'Failed to preview recipients', 'error');
    } finally {
      setPreviewing(false);
    }
  };

  /* ── save ──────────────────────────────────────────────────────────── */
  const handleSave = async () => {
    if (!form.name.trim() || !form.titleEn.trim() || !form.bodyEn.trim()) {
      notify('Name, English title and English body are required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (form.id) {
        await NotificationAdminService.updateCampaign(form.id, buildPayload());
        notify('Campaign updated');
      } else {
        await NotificationAdminService.createCampaign(buildPayload());
        notify('Campaign created');
      }
      setDialogOpen(false);
      loadCampaigns();
    } catch (e) {
      notify(e?.message || 'Failed to save campaign', 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ── send / delete ─────────────────────────────────────────────────── */
  const handleSend = async (c) => {
    if (!window.confirm(`Send campaign "${c.name}" now? This cannot be undone.`)) return;
    try {
      await NotificationAdminService.sendCampaign(c.id);
      notify('Campaign sending started');
      loadCampaigns();
    } catch (e) { notify(e?.message || 'Failed to send campaign', 'error'); }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete campaign "${c.name}"?`)) return;
    try {
      await NotificationAdminService.deleteCampaign(c.id);
      notify('Campaign deleted');
      loadCampaigns();
    } catch (e) { notify(e?.message || 'Failed to delete campaign', 'error'); }
  };

  /* ── team name strings ─────────────────────────────────────────────── */
  const teamNameOptions = allTeams.map((t) => t.name ?? t).filter(Boolean);

  /* ── render ────────────────────────────────────────────────────────── */
  return (
    <Box>
      {/* ── page header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CampaignIcon sx={{ color: 'primary.main' }} />
          <Box>
            <Typography variant="h5" fontWeight={600}>Mass Communication</Typography>
            <Typography variant="body2" color="text.secondary">
              Create and send targeted notifications to segments of users.
            </Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          New Campaign
        </Button>
      </Box>

      {/* ── campaigns table ── */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Recipients</TableCell>
                <TableCell>Sent</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    No campaigns yet. Create your first one.
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((c) => {
                  const isDraft = c.status === 'DRAFT';
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>{c.titleEn}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={c.status} color={statusColor(c.status)} />
                      </TableCell>
                      <TableCell>{c.recipientCount ?? 0}</TableCell>
                      <TableCell>{c.sentCount ?? 0}</TableCell>
                      <TableCell>
                        {c.createdAt ? new Date(c.createdAt).toLocaleString() : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={isDraft ? 'Edit' : 'Only drafts can be edited'}>
                          <span>
                            <IconButton size="small" disabled={!isDraft} onClick={() => openEdit(c)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isDraft ? 'Send now' : 'Already sent'}>
                          <span>
                            <IconButton size="small" color="primary" disabled={!isDraft} onClick={() => handleSend(c)}>
                              <SendIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={c.status === 'SENDING'}
                              onClick={() => handleDelete(c)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ══════════════════ Campaign form dialog ══════════════════════════ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          {form.id ? 'Edit Campaign' : 'New Campaign'}
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          {/* ── CONTENT section ───────────────────────────────────────── */}
          <SectionLabel>Content</SectionLabel>
          <Stack spacing={2.5}>

            {/* Campaign name with autocomplete prefill */}
            <Autocomplete
              freeSolo
              options={nameOptions}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
              inputValue={nameInput}
              onInputChange={handleNameInputChange}
              onChange={handleNameSelect}
              renderOption={(props, option) => (
                <li {...props} key={option.campaign?.id ?? option.label}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{option.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {option.campaign?.status} · {option.campaign?.titleEn}
                    </Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Campaign name (internal)"
                  required
                  helperText="Start typing to find and prefill an existing campaign."
                />
              )}
            />

            {/* Title EN */}
            <TextField
              label="Title (English)"
              fullWidth
              required
              value={form.titleEn}
              onChange={handleField('titleEn')}
            />

            {/* Title ES */}
            <TextField
              label="Title (Spanish)"
              fullWidth
              value={form.titleEs}
              onChange={handleField('titleEs')}
            />

            {/* Body EN */}
            <TextField
              label="Body (English)"
              fullWidth
              required
              multiline
              minRows={3}
              value={form.bodyEn}
              onChange={handleField('bodyEn')}
            />

            {/* Body ES */}
            <TextField
              label="Body (Spanish)"
              fullWidth
              multiline
              minRows={3}
              value={form.bodyEs}
              onChange={handleField('bodyEs')}
            />

            {/* Image URL + upload */}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
              <TextField
                label="Image URL"
                fullWidth
                value={form.imageUrl}
                onChange={handleField('imageUrl')}
              />
              <Button
                component="label"
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                disabled={uploading}
                sx={{ whiteSpace: 'nowrap', flexShrink: 0, height: 56 }}
              >
                {uploading ? 'Uploading…' : 'Upload'}
                <input hidden type="file" accept="image/*" onChange={handleUpload} />
              </Button>
            </Box>

            {/* Action URL */}
            <TextField
              label="Action link (deep link)"
              fullWidth
              value={form.actionUrl}
              onChange={handleField('actionUrl')}
              helperText="Where users land on click. Leave blank to default to the notifications page."
            />

            {/* Category */}
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select 
                label="Category" 
                value={form.category} 
                onChange={handleField('category')}
                disabled={categoriesLoading}
              >
                {categories.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.displayName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Priority */}
            <FormControl fullWidth>
              <InputLabel>Priority</InputLabel>
              <Select label="Priority" value={form.priority} onChange={handleField('priority')}>
                {PRIORITIES.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* ── AUDIENCE section ──────────────────────────────────────── */}
          <SectionLabel>Audience targeting (all filters combined with AND)</SectionLabel>
          <Stack spacing={2.5}>

            {/* Teams multi-select */}
            <Autocomplete
              multiple
              options={teamNameOptions}
              loading={teamsLoading}
              value={form.targetTeamNames}
              onChange={(_e, value) => setForm((f) => ({ ...f, targetTeamNames: value }))}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    key={option}
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Teams"
                  helperText="Select one or more teams. Leave empty to target all teams."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {teamsLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* FCY filter */}
            <FormControl fullWidth>
              <InputLabel>First contract year</InputLabel>
              <Select
                label="First contract year"
                value={form.fcyFilter}
                onChange={handleField('fcyFilter')}
              >
                {FCY_FILTERS.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Pin level min */}
            <FormControl fullWidth>
              <InputLabel>Minimum pin level</InputLabel>
              <Select
                label="Minimum pin level"
                value={form.pinRankMin}
                onChange={handleField('pinRankMin')}
              >
                <MenuItem value=""><em>No minimum</em></MenuItem>
                {PIN_LEVELS.map((p) => (
                  <MenuItem key={p.rank} value={p.rank}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Pin level max */}
            <FormControl fullWidth>
              <InputLabel>Maximum pin level</InputLabel>
              <Select
                label="Maximum pin level"
                value={form.pinRankMax}
                onChange={handleField('pinRankMax')}
              >
                <MenuItem value=""><em>No maximum</em></MenuItem>
                {PIN_LEVELS.map((p) => (
                  <MenuItem key={p.rank} value={p.rank}>{p.label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Current pin range summary */}
            {(form.pinRankMin !== '' || form.pinRankMax !== '') && (
              <Alert severity="info" sx={{ py: 0.5 }}>
                Targeting pin levels:{' '}
                <strong>
                  {form.pinRankMin !== '' ? pinLevelByRank[form.pinRankMin]?.label : 'any'}
                </strong>
                {' → '}
                <strong>
                  {form.pinRankMax !== '' ? pinLevelByRank[form.pinRankMax]?.label : 'any'}
                </strong>
              </Alert>
            )}

            {/* Explicit IBO numbers */}
            <TextField
              label="Explicit IBO numbers"
              fullWidth
              value={form.targetIboNumbers}
              onChange={handleField('targetIboNumbers')}
              helperText="Comma-separated IBO numbers. Added in addition to the segment above."
            />

            {/* Preview */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, pt: 0.5 }}>
              <Button
                variant="outlined"
                startIcon={previewing ? <CircularProgress size={16} /> : <GroupsIcon />}
                onClick={handlePreview}
                disabled={previewing}
              >
                {previewing ? 'Calculating…' : 'Preview recipient count'}
              </Button>
              {previewCount !== null && (
                <Chip
                  color={previewCount === 0 ? 'warning' : 'info'}
                  label={`${previewCount} recipient${previewCount !== 1 ? 's' : ''}`}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving
              ? 'Saving…'
              : form.id
              ? 'Save changes'
              : 'Create draft'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── snackbar ── */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MassCommunicationPage;
